import { redirect } from '@tanstack/react-router';
import { fetchPermissions, requireRoutePermission } from '@/lib/route-guard';
import { firstAccessibleSetupUrl, setupNavItems } from '@/lib/setup-nav';

/**
 * Route-level permission guard for /setup/* pages. The required permission
 * is looked up from `setupNavItems`, so the nav and the route guard can
 * never disagree about who may open a page.
 *
 * Call from a route's `beforeLoad`:
 *   beforeLoad: () => requireSetupPageAccess('/setup/locations')
 *
 * Behaviour:
 * - System admins always pass.
 * - If the user has the page's permission, returns silently.
 * - If not, throws a redirect to the dashboard (`/`). Used because the user
 *   may also lack permission for other setup pages, so redirecting within
 *   /setup could loop.
 *
 * Why this matters: SetupLayout already hides nav items the user can't see,
 * but a determined user can still type the URL directly. The backend always
 * 403s the data — but they'd see a partially-loaded page with an error
 * toast, which is leaky UX.
 *
 * Cache: shares `mePermissionsQueryOptions` with `useCurrentPermissions`,
 * so the React tree gets the same data without a second fetch.
 */
export async function requireSetupPageAccess(url: string): Promise<void> {
  const item = setupNavItems.find((i) => i.url === url);
  if (!item) {
    // Misconfiguration (route exists but has no nav entry) — fail closed.
    throw redirect({ to: '/' });
  }
  return requireRoutePermission(item.permission);
}

/**
 * Redirect a bare /setup visit to the first setup page the user can
 * actually open (nav order), or to the dashboard when they have none.
 * Always throws a redirect — call it from `beforeLoad`.
 */
export async function redirectToAccessibleSetupPage(): Promise<never> {
  const data = await fetchPermissions();
  const target = data
    ? firstAccessibleSetupUrl(new Set(data.permissions), data.isSystemAdmin)
    : null;
  throw redirect({ to: target ?? '/' });
}
